import { ReactNode, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import "./index.scss";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  style?: string;
  theme?: "light" | "dark";
  openOnRight?: boolean;
  openBottom?: boolean;
  closeOnOutsideClick?: boolean;
  /** This is to enable ref for alerts modal */
  referenceElement?: React.RefObject<HTMLElement>;
  /** This is to enable dynamic css for alerts modal */
  isEnableDynamicPositionCss?: boolean;
}

export const Modal = ({
  isOpen,
  onClose,
  children,
  style,
  theme,
  openOnRight,
  openBottom,
  closeOnOutsideClick,
  referenceElement,
  isEnableDynamicPositionCss,
}: IProps) => {
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  // Note: this effect to positioning alerts modal relatively to the bell icon
  useEffect(() => {
    if (isOpen && referenceElement?.current) {
      const rect = referenceElement.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 10, // 10px below the reference element
        left: rect.left - 440, // Left to the width of alert modal
      });
    }
  }, [isOpen, referenceElement]);

  const modalContent = (
    <div
      className={classNames(
        `modal`,
        isOpen ? "open" : "",
        `theme-${theme}`,
        openOnRight ? "open-right" : "",
        openBottom ? "open-bottom" : "",
        style ? `${style}` : ""
      )}
      onClick={closeOnOutsideClick ? onClose : () => {}}
    >
      <div
        className={classNames({
          modal__content: true,
          "center-static-modal":
            isOpen && !isEnableDynamicPositionCss && !openOnRight,
        })}
        onClick={(e) => e.stopPropagation()}
        style={
          isEnableDynamicPositionCss
            ? {
                top: `${position.top}px`,
                left: `${position.left}px`,
                position: "absolute",
              }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  );

  return isOpen ? ReactDOM.createPortal(modalContent, document.body) : null;
};
